import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, Settings2, MessageSquareQuote, LogOut, ExternalLink, Loader2, UserCog, Images, Store } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/site/Logo";
import { Overview } from "../components/admin/Overview";
import { LeadsPanel } from "../components/admin/LeadsPanel";
import { SettingsPanel } from "../components/admin/SettingsPanel";
import { TestimonialsPanel } from "../components/admin/TestimonialsPanel";
import { TeamPanel } from "../components/admin/TeamPanel";
import { FollowUps } from "../components/admin/FollowUps";
import { GalleryPanel } from "../components/admin/GalleryPanel";
import { ShopsPanel } from "../components/admin/ShopsPanel";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["leads", "Leads", Users],
  ["team", "Team", UserCog],
  ["shops", "Partner shops", Store],
  ["settings", "Site & automation", Settings2],
  ["stories", "Testimonials", MessageSquareQuote],
  ["gallery", "Gallery", Images],
];

const AGENT_TABS = [["leads", "My leads", Users]];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      if (r.data?.role === "agent") setTab("leads");
    }).catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("iph_token");
    nav("/admin/login");
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="animate-spin text-saffron" /></div>;
  const isAdmin = user.role === "admin";
  const tabs = isAdmin ? TABS : AGENT_TABS;
  const title = tabs.find((x) => x[0] === tab)?.[1] ?? "";

  return (
    <div className="min-h-screen bg-ink text-white lg:flex" data-testid="admin-dashboard">
      <aside className="border-b border-white/10 bg-card/60 lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:flex-col lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between p-5 lg:p-6"><Logo /></div>
        <nav className="no-scrollbar flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-1 lg:flex-col lg:pb-0">
          {tabs.map(([k, label, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex shrink-0 items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-300 ${tab === k ? "bg-saffron text-ink" : "text-slate-300 hover:bg-white/5"}`}>
              <I size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="hidden space-y-1 border-t border-white/10 p-3 lg:block">
          <Link to="/" target="_blank" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-slate-300 hover:bg-white/5" data-testid="admin-view-site-link"><ExternalLink size={17} /> View site</Link>
          <button onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-slate-300 hover:bg-white/5" data-testid="admin-logout-btn"><LogOut size={17} /> Log out</button>
        </div>
      </aside>
      <main className="flex-1 p-5 sm:p-8 lg:p-10">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-300" data-testid="dashboard-role-label">{isAdmin ? "Control centre" : `Team workspace · ${user.name}`}</p>
            <h1 className="mt-1 font-display text-3xl font-bold" data-testid="admin-page-title">{title}</h1>
          </div>
          <button onClick={logout} className="btn-ghost lg:hidden" data-testid="admin-logout-mobile-btn"><LogOut size={16} /></button>
        </div>
        {isAdmin && tab === "overview" && <Overview />}
        {tab === "leads" && !isAdmin && <div className="mb-6"><FollowUps /></div>}
        {tab === "leads" && <LeadsPanel isAdmin={isAdmin} />}
        {isAdmin && tab === "team" && <TeamPanel />}
        {isAdmin && tab === "settings" && <SettingsPanel />}
        {isAdmin && tab === "stories" && <TestimonialsPanel />}
        {isAdmin && tab === "gallery" && <GalleryPanel />}
        {isAdmin && tab === "shops" && <ShopsPanel />}
      </main>
    </div>
  );
}
